import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { useCallback, useRef } from 'react';

const MAX_OVERFLOW = 60;

function decay(value, max) {
  if (max === 0) return 0;
  const entry = value / max;
  const sigmoid = 2 * (1 / (1 + Math.exp(-entry)) - 0.5);
  return sigmoid * max;
}

export default function ElasticResizeHandle({ onDrag, value, min, max, direction = 'right', externalOverflow }) {
  const internalOverflow = useMotionValue(0);
  const overflow = externalOverflow ?? internalOverflow;
  const isDragging = useMotionValue(false);
  const hover = useMotionValue(0);
  const hoverAnimRef = useRef(null);

  const pillHeight = useTransform(overflow, [-MAX_OVERFLOW, 0, MAX_OVERFLOW], [144, 72, 144]);
  const pillScaleY = useTransform(overflow, [-MAX_OVERFLOW, 0, MAX_OVERFLOW], [1.6, 1, 1.6]);
  const pillColor = useTransform([overflow, hover], ([o, h]) => {
    const overflowMix = Math.min(1, Math.abs(o) / 10);
    const mix = Math.max(overflowMix, h);
    return `color-mix(in srgb, var(--clr-primary) ${mix * 100}%, var(--border-subtle))`;
  });
  const pillOpacity = useTransform([overflow, hover], ([o, h]) => {
    const overflowOp = Math.min(1, 0.45 + (Math.abs(o) / MAX_OVERFLOW) * 0.55);
    const hoverOp = 0.45 + h * 0.55;
    return Math.max(overflowOp, hoverOp);
  });

  const handleHoverStart = useCallback(() => {
    if (hoverAnimRef.current) hoverAnimRef.current.stop();
    hoverAnimRef.current = animate(hover, 1, { duration: 0.15 });
  }, [hover]);

  const handleHoverEnd = useCallback(() => {
    if (hoverAnimRef.current) hoverAnimRef.current.stop();
    hoverAnimRef.current = animate(hover, 0, { duration: 0.2 });
  }, [hover]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startValue = value;
    isDragging.set(true);

    const onMove = (ev) => {
      const delta = direction === 'right'
        ? ev.clientX - startX
        : startX - ev.clientX;

      const rawNext = startValue + delta;
      const clamped = Math.max(min, Math.min(max, rawNext));
      onDrag(clamped);

      // compute overflow past the limit
      let excess = 0;
      if (rawNext < min) excess = rawNext - min;       // negative
      else if (rawNext > max) excess = rawNext - max;  // positive

      overflow.set(decay(excess, MAX_OVERFLOW));
    };

    const onUp = () => {
      isDragging.set(false);
      animate(overflow, 0, { type: 'spring', bounce: 0.5, duration: 0.6 });
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [value, min, max, direction, onDrag, overflow, isDragging]);

  return (
    <div
      className="studio-resize-handle"
      onMouseDown={handleMouseDown}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
    >
      <motion.div
        className="elastic-handle-pill"
        style={{
          height: pillHeight,
          scaleY: pillScaleY,
          backgroundColor: pillColor,
          opacity: pillOpacity,
        }}
      />
    </div>
  );
}
